import React from "react";
import { SkeletonRows } from "../../../Components/Common/LoadingStates";
import { chainName } from "../constants";
import { shortAddress, formatDateTime, sponsorshipState, rowActions } from "../utils";
import { StatusBadge, SponsorBadge, CategoryChips, TokenLogo } from "./Badges";

const ACTION_LABEL = {
  edit: "Edit",
  publish: "Publish",
  delist: "Delist",
  relist: "Relist",
  delete: "Delete",
  approve: "Approve sponsorship",
  revoke: "Revoke sponsorship",
};

const ACTION_CLS = {
  delist: "text-red-300 border-red-500/30 hover:bg-red-500/10",
  delete: "text-red-300 border-red-500/30 hover:bg-red-500/10",
  approve: "text-green-300 border-green-500/30 hover:bg-green-500/10",
  revoke: "text-amber-300 border-amber-400/30 hover:bg-amber-400/10",
};

const th = "px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap";
const td = "px-3 py-3 text-sm text-gray-100 align-top";

const COLS = 10;

export default function ListingsTable({ listings, loading, canManage, canSponsor, busyId, onAction }) {
  const now = new Date();
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-bl-accent/5 border-b border-gray-700">
          <tr>
            <th className={th}>Token</th>
            <th className={th}>Chain</th>
            <th className={th}>Contract</th>
            <th className={th}>Status</th>
            <th className={th}>Verified</th>
            <th className={th}>Featured</th>
            <th className={th}>Sponsored</th>
            <th className={th}>Categories</th>
            <th className={th}>Updated</th>
            <th className={th}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {loading && listings.length === 0 && <SkeletonRows rows={6} cols={COLS} />}
          {!loading && listings.length === 0 && (
            <tr>
              <td colSpan={COLS} className="px-4 py-12 text-center text-sm text-gray-400">No listings match these filters.</td>
            </tr>
          )}
          {listings.map((l) => {
            const sState = sponsorshipState(l, now);
            const actions = rowActions(l, { canManage, canSponsor, now });
            const busy = busyId === l.id;
            const sponsorTitle = l.sponsor_name
              ? `${l.sponsor_name}${l.sponsored_from ? `, ${formatDateTime(l.sponsored_from)}` : ""}${l.sponsored_until ? ` to ${formatDateTime(l.sponsored_until)}` : ""}`
              : undefined;
            return (
              <tr key={l.id} className={busy ? "opacity-60" : ""}>
                <td className={td}>
                  <div className="flex items-center gap-2.5 min-w-[160px]">
                    <TokenLogo url={l.logo_url} symbol={l.symbol} />
                    <div className="min-w-0">
                      <div className="font-medium truncate max-w-[200px]" title={l.name}>{l.name || "-"}</div>
                      <div className="text-xs text-gray-400">{l.symbol || "-"}</div>
                    </div>
                  </div>
                </td>
                <td className={`${td} whitespace-nowrap`}>{chainName(l.network_id)}</td>
                <td className={`${td} font-mono text-xs whitespace-nowrap`} title={l.contract_address}>{shortAddress(l.contract_address)}</td>
                <td className={td}><StatusBadge status={l.status} /></td>
                <td className={td}>
                  {l.verified ? (
                    <span className="inline-flex items-center gap-1 text-green-300 text-xs"><i className="ri-checkbox-circle-line"></i>Verified</span>
                  ) : (
                    <span className="text-gray-500 text-xs">No</span>
                  )}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {l.featured ? <span className="text-xs">{l.featured_rank ? `Rank ${l.featured_rank}` : "Yes"}</span> : <span className="text-gray-500">-</span>}
                </td>
                <td className={td}>
                  <SponsorBadge state={sState} title={sponsorTitle} />
                  {l.sponsor_name && <div className="text-xs text-gray-400 mt-1 truncate max-w-[160px]" title={sponsorTitle}>{l.sponsor_name}</div>}
                </td>
                <td className={td}><CategoryChips categories={l.categories} /></td>
                <td className={`${td} whitespace-nowrap`}>
                  <div className="text-xs">{formatDateTime(l.updated_at)}</div>
                  {l.updated_by && <div className="text-xs text-gray-400 truncate max-w-[180px]" title={l.updated_by}>{l.updated_by}</div>}
                </td>
                <td className={td}>
                  <div className="flex flex-wrap gap-1.5 min-w-[150px]">
                    {actions.map((a) => (
                      <button
                        key={a}
                        type="button"
                        disabled={busy}
                        onClick={() => onAction(a, l)}
                        className={`px-2 py-1 text-xs rounded-md border transition-colors disabled:opacity-50 whitespace-nowrap ${ACTION_CLS[a] || "text-gray-200 border-white/10 hover:bg-white/[0.04]"}`}
                      >
                        {ACTION_LABEL[a]}
                      </button>
                    ))}
                    {!canSponsor && sState === "pending" && (
                      <span className="text-xs text-amber-300 self-center whitespace-nowrap" title="A role with sponsorship approval must approve this campaign">
                        Pending approval
                      </span>
                    )}
                    {actions.length === 0 && !(sState === "pending" && !canSponsor) && <span className="text-xs text-gray-500">View only</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
